/**
 * Pure helpers for the rules page (spec 2026-10-27-21): filters by class,
 * universe and text, the recurrence of a periodic rule, the organization's
 * tuning of a shipped rule, and the creation dialog's three shapes compiled
 * to the rule vocabulary.
 */

export const UNIVERSES = ['condominium', 'commercial', 'residential_rental', 'social_housing'] as const
export type Universe = (typeof UNIVERSES)[number]

export const RULE_CLASSES = ['regulatory', 'periodic_inspection', 'management'] as const
export type RuleClassFilter = 'all' | (typeof RULE_CLASSES)[number]

/** The rule shape these helpers read. */
export interface CatalogRule {
  uid: string
  code?: string
  label?: Record<string, string>
  rule_class?: string
  universes?: string[] | null
  check?: Record<string, unknown>
  periodicity?: Record<string, unknown> | null
  frequency_months?: number | null
  tuning?: Record<string, TuningEntry> | null
  origin?: string
}

export interface TuningEntry {
  shipped: unknown
  value: unknown
  by?: number | null
  at?: string
}

export interface RuleFilters {
  klass: RuleClassFilter
  universe: Universe | null
  strict: boolean
  q: string
}

export const EMPTY_FILTERS: RuleFilters = { klass: 'all', universe: null, strict: false, q: '' }

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** Keeps the rules matching the filters: class, universe (tagged plus general,
 * or strictly tagged) and a text matched on the code and every label. */
export function filterRules<R extends CatalogRule>(rules: R[], f: RuleFilters): R[] {
  const q = normalize(f.q.trim())
  return rules.filter((r) => {
    if (f.klass !== 'all' && (r.rule_class ?? 'regulatory') !== f.klass) return false
    if (f.universe) {
      const u = r.universes ?? []
      if (f.strict ? !u.includes(f.universe) : u.length > 0 && !u.includes(f.universe)) return false
    }
    if (q) {
      const hay = [r.code ?? '', ...Object.values(r.label ?? {})].map(normalize).join(' ')
      if (!hay.includes(q)) return false
    }
    return true
  })
}

/** Reads the filters from URL search params (`class`, `universe`, `strict`, `q`). */
export function filtersFromSearch(search: Record<string, unknown>): RuleFilters {
  const klass = typeof search.class === 'string' && (RULE_CLASSES as readonly string[]).includes(search.class)
    ? (search.class as RuleClassFilter)
    : 'all'
  const universe = typeof search.universe === 'string' && (UNIVERSES as readonly string[]).includes(search.universe)
    ? (search.universe as Universe)
    : null
  const strict = search.strict === true || search.strict === 'true'
  const q = typeof search.q === 'string' ? search.q : search.q != null ? String(search.q) : ''
  return { klass, universe, strict, q }
}

/** Writes the filters as search params, leaving defaults out. */
export function filtersToSearch(f: RuleFilters): Record<string, string | undefined> {
  return {
    class: f.klass === 'all' ? undefined : f.klass,
    universe: f.universe ?? undefined,
    strict: f.universe && f.strict ? 'true' : undefined,
    q: f.q.trim() ? f.q : undefined,
  }
}

/** The cadences offered as "N fois par an"; any other is typed in months. */
export const TIMES_PER_YEAR = [1, 2, 3, 4, 6, 12] as const

/** The rule's default recurrence in months, null when it has none or when it
 * is a sweep carrier or a windowed count (not a periodic obligation). */
export function recurrenceOf(rule: CatalogRule): number | null {
  const check = rule.check ?? {}
  if (check.producer === 'sweep' || check.aggregate) return null
  const def = rule.periodicity?.default_months
  if (typeof def === 'number' && def > 0) return def
  if (typeof rule.frequency_months === 'number' && rule.frequency_months > 0) return rule.frequency_months
  return null
}

/** "N fois par an" for a recurrence that divides a year evenly, else null. */
export function timesPerYear(months: number): number | null {
  if (months <= 0 || 12 % months !== 0) return null
  const n = 12 / months
  return (TIMES_PER_YEAR as readonly number[]).includes(n) ? n : null
}

/** The conditional clauses of a rule's periodicity (shown read-only). */
export function conditionalClauses(rule: CatalogRule): { months: number; when: Record<string, unknown> }[] {
  const rules = rule.periodicity?.rules
  if (!Array.isArray(rules)) return []
  return rules
    .filter((c): c is { months: number; when: Record<string, unknown> } =>
      !!c && typeof c === 'object' && typeof (c as { months?: unknown }).months === 'number')
    .map((c) => ({ months: c.months, when: c.when ?? {} }))
}

/** The fields an organization tuned on a shipped rule, sorted. */
export function tunedFields(rule: CatalogRule): string[] {
  return Object.keys(rule.tuning ?? {}).sort()
}

/** "champ : livré → réglé" lines for the "Modifiée" tooltip. */
export function tuningSummary(rule: CatalogRule): string[] {
  const t = rule.tuning ?? {}
  return tunedFields(rule).map((f) => `${f}: ${String(t[f].shipped)} → ${String(t[f].value)}`)
}

export const DRAFT_SHAPES = ['recurring_obligation', 'occurrence_threshold', 'data_value'] as const
export type DraftShape = (typeof DRAFT_SHAPES)[number]

export const DRAFT_OPERATORS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_set', 'not_set'] as const

/** The creation dialog's form (and a model's draft, once loaded into it). */
export interface RuleForm {
  shape: DraftShape
  labelFr: string
  labelEn: string
  descriptionFr: string
  descriptionEn: string
  property: string
  documentTypes: string[]
  months: number
  warnWindowDays: number
  inspection: boolean
  interventionKind: string
  count: number
  windowMonths: number
  operator: string
  value: string
  severity: number
  universes: Universe[]
  autoCreateIssue: boolean
}

export const EMPTY_FORM: RuleForm = {
  shape: 'recurring_obligation',
  labelFr: '',
  labelEn: '',
  descriptionFr: '',
  descriptionEn: '',
  property: '',
  documentTypes: [],
  months: 12,
  warnWindowDays: 30,
  inspection: false,
  interventionKind: 'corrective',
  count: 3,
  windowMonths: 12,
  operator: 'eq',
  value: '',
  severity: 3,
  universes: [],
  autoCreateIssue: true,
}

/** The body fields a form compiles to, the server's CompileDraft in the
 * browser: the rule vocabulary, no new evaluator. */
export interface CompiledRule {
  rule_class: 'regulatory' | 'periodic_inspection' | 'management'
  label: Record<string, string>
  description: Record<string, string>
  conditions: Record<string, unknown>
  check: Record<string, unknown>
  periodicity?: Record<string, unknown>
  frequency_months?: number
  warn_window_days?: number
  evidence_mode?: 'auto' | 'document'
  satisfied_by_document_types?: string[]
  severity: number
  universes: string[]
  auto_create_issue: boolean
}

function typedValue(v: string): unknown {
  const s = v.trim()
  if (s !== '' && Number.isFinite(Number(s))) return Number(s)
  if (s === 'true') return true
  if (s === 'false') return false
  return s
}

/** Compiles the form, or returns the reason it cannot be. */
export function compileRuleForm(f: RuleForm): CompiledRule | { error: string } {
  const label: Record<string, string> = {}
  if (f.labelFr.trim()) label.fr = f.labelFr.trim()
  if (f.labelEn.trim()) label.en = f.labelEn.trim()
  if (!label.fr && !label.en) return { error: 'label' }
  const description: Record<string, string> = {}
  if (f.descriptionFr.trim()) description.fr = f.descriptionFr.trim()
  if (f.descriptionEn.trim()) description.en = f.descriptionEn.trim()
  const base = {
    rule_class: 'management' as CompiledRule['rule_class'],
    label,
    description,
    conditions: {},
    severity: f.severity,
    universes: f.universes,
    auto_create_issue: f.autoCreateIssue,
  }
  switch (f.shape) {
    case 'recurring_obligation': {
      if (!(f.months >= 1 && f.months <= 600)) return { error: 'months' }
      if (!f.property.trim() && f.documentTypes.length === 0) return { error: 'proof' }
      return {
        ...base,
        rule_class: (f.inspection ? 'periodic_inspection' : 'management') as CompiledRule['rule_class'],
        check: f.property.trim() ? { property: f.property.trim(), op: 'within_months', value: f.months } : {},
        evidence_mode: (f.property.trim() ? 'auto' : 'document') as 'auto' | 'document',
        periodicity: { default_months: f.months },
        frequency_months: f.months,
        warn_window_days: f.warnWindowDays > 0 ? f.warnWindowDays : undefined,
        satisfied_by_document_types: f.documentTypes.length > 0 ? f.documentTypes : undefined,
      }
    }
    case 'occurrence_threshold': {
      if (!(f.windowMonths >= 1 && f.windowMonths <= 120)) return { error: 'window' }
      if (!(f.count >= 0)) return { error: 'count' }
      const check: Record<string, unknown> = {
        aggregate: 'count_over_window', source: 'interventions', window_months: f.windowMonths, op: 'lte', value: f.count,
      }
      if (f.interventionKind) check.kind = f.interventionKind
      return { ...base, check }
    }
    case 'data_value': {
      if (!f.property.trim()) return { error: 'property' }
      if (!(DRAFT_OPERATORS as readonly string[]).includes(f.operator)) return { error: 'operator' }
      const check: Record<string, unknown> = { property: f.property.trim(), op: f.operator }
      if (f.operator !== 'is_set' && f.operator !== 'not_set') {
        if (!f.value.trim()) return { error: 'value' }
        check.value = typedValue(f.value)
      }
      return { ...base, check }
    }
  }
}

/** A model's draft (GET /compliance/rules/drafts/{job}) loaded into the form. */
export interface ServerDraft {
  shape?: string
  label_fr?: string
  label_en?: string
  description_fr?: string
  description_en?: string
  asset_type_code?: string
  property?: string
  document_types?: string[]
  months?: number
  warn_window_days?: number
  inspection?: boolean
  intervention_kind?: string
  count?: number
  window_months?: number
  operator?: string
  value?: unknown
  severity?: number
}

export function formFromDraft(d: ServerDraft, prev: RuleForm = EMPTY_FORM): RuleForm {
  const shape = (DRAFT_SHAPES as readonly string[]).includes(d.shape ?? '') ? (d.shape as DraftShape) : prev.shape
  return {
    ...prev,
    shape,
    labelFr: d.label_fr ?? prev.labelFr,
    labelEn: d.label_en ?? prev.labelEn,
    descriptionFr: d.description_fr ?? prev.descriptionFr,
    descriptionEn: d.description_en ?? prev.descriptionEn,
    property: d.property ?? '',
    documentTypes: d.document_types ?? [],
    months: d.months || prev.months,
    warnWindowDays: d.warn_window_days ?? prev.warnWindowDays,
    inspection: !!d.inspection,
    interventionKind: d.intervention_kind ?? prev.interventionKind,
    count: d.count ?? prev.count,
    windowMonths: d.window_months || prev.windowMonths,
    operator: d.operator || prev.operator,
    value: d.value == null ? '' : String(d.value),
    severity: d.severity || prev.severity,
  }
}
